import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './CustomSelect.module.css';

interface SelectOption {
  value: string;
  label: string;
}

interface CustomSelectProps {
  value: string;
  onChange: (event: { target: { value: string } }) => void;
  options: SelectOption[];
  disabled?: boolean;
  placeholder?: string;
  [key: string]: any;
}

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  disabled,
  placeholder = 'Select...',
  ...props
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [position, setPosition] = useState<DropdownPosition>({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        const dropdown = document.querySelector('[data-custom-select-dropdown]');
        if (dropdown && !dropdown.contains(event.target as Node)) {
          setIsOpen(false);
        }
      }
    };

    const handleScroll = () => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    if (isOpen) {
      // Delay to prevent immediate closing on the same click that opened it
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen]);

  const handleSelect = (option: SelectOption, event: React.MouseEvent) => {
    event.stopPropagation();
    onChange({ target: { value: option.value } });
    setIsOpen(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        className={styles.selectButton}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setIsOpen(!isOpen);
        }}
        disabled={disabled}
        type="button"
        {...props}
      >
        <span>{displayText}</span>
        <span className={styles.arrow} data-open={isOpen ? 'true' : 'false'} aria-hidden="true">
          ▼
        </span>
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            className={styles.dropdownPortal}
            data-custom-select-dropdown
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: `${position.width}px`,
            }}
          >
            {options.map((option) => (
              <div
                key={option.value}
                className={styles.option}
                data-selected={option.value === value ? 'true' : 'false'}
                onClick={(e) => handleSelect(option, e)}
              >
                {option.label}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};
